import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogClose, WizardDialogContent } from "@hulahoop/ui";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { useAddBeneficiary } from "@/application/workflows/beneficiaries/useAddBeneficiary";
import type { CreateBeneficiaryResponse } from "@/domain/beneficiary";
import {
  BENEFICIARY_WIZARD_STEP_IDS,
  createBeneficiaryValidationMessages,
} from "@/domain/validation/beneficiarySchemas";
import { useToastStore } from "@/stores/useToastStore";
import { CreateBeneficiaryStepFields } from "../components/CreateBeneficiaryStepFields";

export function CreateBeneficiaryWizardModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (beneficiary: CreateBeneficiaryResponse) => void;
}) {
  const { t } = useTranslation();
  const messages = useMemo(() => createBeneficiaryValidationMessages(t), [t]);
  const wizard = useAddBeneficiary({ open, messages });
  const showToast = useToastStore((state) => state.showToast);
  const steps = BENEFICIARY_WIZARD_STEP_IDS.map((step) => ({
    label: t(`promoters.wizard.steps.${step}`),
    description: t(`promoters.wizard.steps.${step}_description`),
  }));
  const handleCreate = async () => {
    const outcome = await wizard.submit();
    if (outcome.ok) {
      showToast(t("promoters.wizard.created", { name: outcome.value.name }), "success");
      onCreated(outcome.value);
      onOpenChange(false);
      return;
    }
    const code = outcome.error instanceof ApiClientError ? outcome.error.code : null;
    if (code === "beneficiary_incentive_type_required") {
      showToast(t("promoters.errors.incentive_required"), "error");
    } else if (outcome.error instanceof ApiClientError) {
      showToast(t("promoters.errors.create_failed"), "error");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <WizardDialogContent
        title={t("promoters.wizard.title")}
        description={t("promoters.wizard.description")}
        steps={steps}
        value={wizard.step}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="ghost" disabled={wizard.step === 0} onClick={wizard.back}>
              {t("common.previous")}
            </Button>
            <DialogClose render={<Button variant="outline">{t("common.cancel")}</Button>} />
            {wizard.last ? (
              <Button
                disabled={wizard.isSubmitting || !wizard.canContinue}
                onClick={() => void handleCreate()}
              >
                {t("promoters.wizard.create")}
              </Button>
            ) : (
              <Button disabled={!wizard.canContinue} onClick={() => void wizard.next()}>
                {t("promoters.wizard.continue")}
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...wizard.form}>
          <CreateBeneficiaryStepFields step={wizard.step} />
        </FormProvider>
      </WizardDialogContent>
    </Dialog>
  );
}
